import { useEffect, useState } from 'react';
import { copy } from '../../copy/es';
import { api, textoDeError } from '../../lib/api';
import { useSesion } from '../sesion';
import { PeriodoSelect } from '../PeriodoSelect';
import { fechaHora, nombrePeriodo, numero, periodoCierre } from '../../lib/formato';

type Estado = 'descargo' | 'entro_sin_descargar' | 'nunca_abrio' | 'sin_documentos';
interface Fila {
  destinatarioId: string;
  correo: string;
  tercero: string;
  centrosCosto: string[];
  estado: Estado;
  rebote: boolean;
  enviadoEn: string | null;
  ingresoEn: string | null;
  descargaEn: string | null;
  ultimoEventoEn: string | null;
  descargas: number;
  documentos: number;
}
interface Panel {
  periodo: string;
  publicada: boolean;
  filas: Fila[];
  resumen: {
    destinatarios: number;
    documentos_publicados: number;
    descargo: number;
    entro_sin_descargar: number;
    nunca_abrio: number;
    sin_documentos?: number;
    rebotes: number;
    documentos_faltantes: number;
  };
}

/** Mockup screen 3: KPIs with the middle state highlighted, evidence table, resend per row, export link. */
export function EntregasPage() {
  const { usuario } = useSesion();
  const c = copy.admin.entregas;
  const [periodo, setPeriodo] = useState(periodoCierre());
  const [panel, setPanel] = useState<Panel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Estado | 'todos'>('todos');
  const [verTodos, setVerTodos] = useState(false);

  async function cargar(p = periodo) {
    setError(null);
    try {
      setPanel(await api<Panel>(`/api/admin/periodos/${p}/entregas`));
    } catch (e) {
      setError(textoDeError(e));
    }
  }
  useEffect(() => {
    void cargar(periodo);
  }, [periodo]);

  async function reenviar(f: Fila) {
    setAviso(null);
    setError(null);
    try {
      const r = await api<{ mensaje: string }>(
        `/api/admin/periodos/${periodo}/entregas/${f.destinatarioId}/reenvio`,
        { method: 'POST' },
      );
      setAviso(r.mensaje);
      await cargar();
    } catch (e) {
      setError(textoDeError(e));
    }
  }

  const todas = panel?.filas.filter((f) => filtro === 'todos' || f.estado === filtro) ?? [];
  const filas = verTodos ? todas : todas.slice(0, 8);
  const pill: Record<Estado, string> = {
    descargo: 'p-ok',
    entro_sin_descargar: 'p-mid',
    nunca_abrio: 'p-no',
    sin_documentos: 'p-no',
  };
  const kpi = (k: Estado | 'todos', n: number, l: string, hl = false) => (
    <button
      key={k}
      className={`kpi ${hl ? 'hl' : ''} ${filtro === k && k !== 'todos' ? 'sel' : ''}`}
      onClick={() => setFiltro(k)}
    >
      <div className="n">{numero(n)}</div>
      <div className="l">{l}</div>
    </button>
  );

  return (
    <div>
      <h2>
        {c.titulo} · {nombrePeriodo(periodo).toLowerCase()}
      </h2>
      <p className="sub">{c.subtitulo}</p>
      <PeriodoSelect
        id="periodo-entregas"
        etiqueta={c.periodo}
        valor={periodo}
        onChange={setPeriodo}
      />
      {error && (
        <div className="ok-box bad" role="alert">
          {error}
        </div>
      )}
      {aviso && (
        <div className="ok-box" role="status">
          {aviso}
        </div>
      )}

      {panel && !panel.publicada && (
        <div className="ctx">
          <div>
            <div className="k">{c.periodo}</div>
            <div className="v">{c.sinPublicar}</div>
          </div>
        </div>
      )}

      {panel?.publicada && (
        <>
          <div className="kpis">
            {kpi('todos', panel.resumen.destinatarios, c.metricas.enviados)}
            {kpi('descargo', panel.resumen.descargo, c.metricas.descargaron)}
            {kpi(
              'entro_sin_descargar',
              panel.resumen.entro_sin_descargar,
              c.metricas.entraron,
              true,
            )}
            {kpi('nunca_abrio', panel.resumen.nunca_abrio, c.metricas.nunca)}
            <div className="kpi" style={{ cursor: 'default' }}>
              <div className="n">{numero(panel.resumen.documentos_publicados)}</div>
              <div className="l">{c.metricas.publicados}</div>
            </div>
            {panel.resumen.documentos_faltantes > 0 && (
              <div className="kpi" style={{ cursor: 'default' }}>
                <div className="n" style={{ color: '#c0392b' }}>
                  {numero(panel.resumen.documentos_faltantes)}
                </div>
                <div className="l">{c.metricas.faltantes}</div>
              </div>
            )}
          </div>
          <table className="mk">
            <thead>
              <tr>
                <th>{c.col.centro}</th>
                <th>{c.col.tercero}</th>
                <th>{c.col.correo}</th>
                <th className="num">{c.col.docs}</th>
                <th>{c.col.enviado}</th>
                <th>{c.col.ingreso}</th>
                <th>{c.col.descarga}</th>
                <th>{c.col.estado}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.destinatarioId}>
                  <td>
                    {f.centrosCosto.map((cc) => (
                      <b key={cc} style={{ display: 'block' }}>
                        {cc}
                      </b>
                    ))}
                  </td>
                  <td>{f.tercero}</td>
                  <td>
                    {f.correo}
                    {f.rebote && (
                      <>
                        {' '}
                        <span className="pill p-bad">{c.rebotado}</span>
                      </>
                    )}
                  </td>
                  <td className="num">{numero(f.documentos)}</td>
                  <td>{fechaHora(f.enviadoEn)}</td>
                  <td>{fechaHora(f.ingresoEn)}</td>
                  <td>{fechaHora(f.descargaEn)}</td>
                  <td>
                    <span className={`pill ${pill[f.estado]}`}>{c.estados[f.estado]}</span>
                  </td>
                  <td>
                    {usuario?.rol === 'operador' && f.estado !== 'descargo' && (
                      <button className="mini" onClick={() => void reenviar(f)}>
                        {f.estado === 'nunca_abrio' ? c.reenviar : c.recordar}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filas.length === 0 && (
                <tr>
                  <td colSpan={8}>{c.sinFilas}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="hist">
            {c.mostrando(filas.length, todas.length)} ·{' '}
            {todas.length > 8 && (
              <button onClick={() => setVerTodos((v) => !v)}>
                {verTodos ? c.verMenos : c.verTodos}
              </button>
            )}
            <a href={`/api/admin/periodos/${periodo}/entregas/exportacion`}>{c.exportar}</a>
          </div>
          <div className="expira">{c.pie}</div>
        </>
      )}
    </div>
  );
}
